// Central data source — SKVY Harmony Placement
// US IT staffing & placement partner for international students, OPT / CPT / H-1B
import {
  Briefcase, Users, Cpu, Database, TrendingUp, Cloud,
  ShieldCheck, Bot, Sparkles, Target, Award,
  Handshake, Code2, GraduationCap, MessageSquare,
  UserCheck, Rocket, BookOpen, PhoneCall, Zap, Brain, LineChart, Settings2, ClipboardList, FileText,
} from "lucide-react";

export const CONTACT = {
  company: "SKVY Harmony Placement",
  legalName: "SKVY Harmony Placement",
  email: "skvyharmony@gmail.com",
  phone: "+1 (707) 225-0156",
  phoneClean: "+17072250156",
  whatsapp: "17072250156",
  address: "5830 E 2nd St, STE 7000 #37343, Casper, WY 82609, USA",
  addressLine1: "5830 E 2nd St, STE 7000 #37343",
  addressLine2: "Casper, Wyoming 82609",
  tagline: "US IT Staffing & Placement for International Students",
};

// FormSubmit endpoint — works without a backend.
// On first submission, FormSubmit sends an activation email to this address.
export const FORM_ENDPOINT = `https://formsubmit.co/${CONTACT.email}`;

// Nav (Resources / Jobs / Dashboard removed per client request)
export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

// ===== Services (9) =====
export const SERVICES = [
  {
    icon: GraduationCap,
    title: "OPT Placement",
    desc: "Get placed in US tech roles that fully sponsor and value your OPT status — with employers who understand STEM extensions.",
    features: ["STEM OPT-friendly employers", "Fast turnaround", "End-to-end coordination"],
  },
  {
    icon: BookOpen,
    title: "CPT Placement",
    desc: "Land internships and co-op roles that count toward your CPT while building the resume that lands the offer.",
    features: ["University-approved employers", "Flexible schedules", "Real project exposure"],
  },
  {
    icon: Award,
    title: "H-1B Jobs",
    desc: "Access a curated network of Fortune-500 and high-growth US employers actively sponsoring H-1B visas.",
    features: ["Verified sponsors", "Cap-exempt options", "Transfer support"],
  },
  {
    icon: Briefcase,
    title: "Full-Time Placement",
    desc: "Direct-hire opportunities across software, data, cloud and cybersecurity — from mid-level to senior IC roles.",
    features: ["FAANG + high-growth SaaS", "Comp benchmarks", "Offer negotiation"],
  },
  {
    icon: Users,
    title: "Contract Jobs",
    desc: "W-2 and C2C contract roles with top US IT consultancies and end clients across the country.",
    features: ["Weekly payroll", "Compliance handled", "Remote & onsite"],
  },
  {
    icon: FileText,
    title: "Resume Building",
    desc: "ATS-optimized resumes rewritten by senior US recruiters — engineered to pass Workday, Greenhouse and Lever screens.",
    features: ["Recruiter-led rewrites", "Role-specific keywords", "Portfolio review"],
  },
  {
    icon: Rocket,
    title: "LinkedIn Optimization",
    desc: "Turn your LinkedIn into an inbound-lead engine — headline, banner, About, experience and recommendations.",
    features: ["Recruiter-magnet headline", "Keyword optimisation", "Content strategy"],
  },
  {
    icon: MessageSquare,
    title: "Mock Interviews",
    desc: "1:1 mock interviews with FAANG-hired mentors — behavioural, coding, system design and case rounds.",
    features: ["FAANG-hired mentors", "Live feedback", "Recorded reviews"],
  },
  {
    icon: PhoneCall,
    title: "Career Consultation",
    desc: "30-minute strategy calls with senior US career advisors — visa strategy, targeting, comp and career design.",
    features: ["Visa strategy", "Role targeting", "90-day plan"],
  },
];

// ===== Industries (8) =====
export const INDUSTRIES = [
  { icon: Code2, name: "Software Development", desc: "Full-stack, mobile, backend, platform engineering", roles: "2,400+ placements" },
  { icon: Database, name: "Data Science", desc: "Analysts, scientists, ML engineers, data platform", roles: "1,180+ placements" },
  { icon: Brain, name: "Artificial Intelligence", desc: "LLMs, NLP, computer vision, MLOps, AI research", roles: "720+ placements" },
  { icon: Cloud, name: "Cloud Computing", desc: "AWS, GCP, Azure — architects, SREs, platform", roles: "1,050+ placements" },
  { icon: ShieldCheck, name: "Cybersecurity", desc: "SOC, GRC, cloud sec, offensive & defensive", roles: "540+ placements" },
  { icon: Settings2, name: "DevOps", desc: "CI/CD, Kubernetes, IaC, observability", roles: "820+ placements" },
  { icon: LineChart, name: "Business Analysis", desc: "BA, product analysts, data-driven PMs", roles: "610+ placements" },
  { icon: ClipboardList, name: "QA Automation", desc: "SDET, Selenium, Cypress, Playwright, performance", roles: "480+ placements" },
];

// ===== Placement Process (7) =====
export const PROCESS_STEPS = [
  { n: "01", title: "Register", desc: "Create your SKVY profile in 90 seconds. Share your resume, visa status, target roles and comp expectations." },
  { n: "02", title: "Resume Review", desc: "A senior US recruiter audits your resume and portfolio and rewrites it for ATS + human readability." },
  { n: "03", title: "AI Skill Matching", desc: "Our engine maps your skills, projects and target roles to 1,200+ US employers actively hiring in your stack." },
  { n: "04", title: "Interview Preparation", desc: "1:1 mock interviews across behavioural, DSA, system design and case rounds with FAANG-hired mentors." },
  { n: "05", title: "Recruiter Submission", desc: "We submit you directly to hiring managers and internal recruiters — no black-box job boards." },
  { n: "06", title: "Job Offer", desc: "We help you negotiate base, bonus, equity and relocation — with real US comp benchmarks." },
  { n: "07", title: "Onboarding", desc: "I-9, background checks, relocation, first-90-days coaching — we stay with you well past day one." },
];

// ===== Why Students Trust Us =====
export const WHY_US = [
  { icon: Zap, title: "Faster Placements", desc: "Curated pipelines cut months of applying — students see interview loops within weeks." },
  { icon: UserCheck, title: "Personalized Career Support", desc: "Every candidate is paired with a dedicated senior recruiter — not a chatbot." },
  { icon: Award, title: "Industry Experts", desc: "Our team hires ex-FAANG, ex-Big-4 and senior US IT staffing veterans." },
  { icon: Handshake, title: "Recruiter Network", desc: "Direct relationships with hiring managers at 1,200+ US employers." },
  { icon: ShieldCheck, title: "Transparent Process", desc: "Regular updates on every submission, interview, offer and status change." },
  { icon: TrendingUp, title: "Long-Term Career Growth", desc: "OPT to H-1B to Green Card — we plan the full arc, not just the first offer." },
];

// ===== Why Choose SKVY Harmony (capabilities on Home) =====
export const CAPABILITIES = [
  { icon: Bot, title: "AI-Powered Job Matching", desc: "Semantic skill-graph matching across 40,000+ live US requisitions in your stack and visa profile." },
  { icon: FileText, title: "Resume Optimization", desc: "ATS-safe rewrites by senior US recruiters — engineered to pass Workday, Greenhouse and Lever screens." },
  { icon: MessageSquare, title: "Interview Preparation", desc: "1:1 mocks with FAANG-hired mentors — behavioural, coding, system design and case rounds." },
  { icon: PhoneCall, title: "Direct Recruiter Connections", desc: "Warm introductions to internal recruiters and hiring managers — no cold applications." },
  { icon: ShieldCheck, title: "Visa-Friendly Opportunities", desc: "Only OPT, STEM-OPT, CPT and H-1B sponsoring employers make it into our shortlist." },
  { icon: Target, title: "Career Mentoring", desc: "Dedicated career coach for role targeting, comp strategy and the multi-year visa journey." },
];

// ===== Testimonials — Indian students placed in US IT jobs =====
export const TESTIMONIALS = [
  {
    quote: "As a Master's student on STEM OPT, I applied to 200+ jobs with no response. Within 5 weeks of joining SKVY Harmony, I had 3 onsite loops and a Software Engineer offer from a Series C fintech in San Francisco. My recruiter literally rewrote my resume line by line.",
    name: "Aarav Kulkarni",
    role: "Software Engineer",
    company: "Series C Fintech, San Francisco",
    visa: "STEM OPT",
    university: "Carnegie Mellon University",
    img: "/students/s1.jpg",
  },
  {
    quote: "SKVY Harmony understood the OPT clock in a way no other agency did. They matched me with a Fortune 100 retailer in Dallas that not only sponsored my H-1B in Year 2 but also filed for my Green Card in Year 3. Life-changing.",
    name: "Ananya Sharma",
    role: "Data Analyst → Senior Data Analyst",
    company: "Fortune 100 Retailer, Dallas",
    visa: "OPT → H-1B",
    university: "University of Texas at Dallas",
    img: "/students/s5.jpg",
  },
  {
    quote: "I moved from a QA contract to a full-time SDET role at a Big-4 consulting firm in Manhattan — all inside my first OPT year. The mock interviews with an ex-Amazon SDET were the single biggest unlock.",
    name: "Priya Iyer",
    role: "SDET II",
    company: "Big-4 Consulting, New York",
    visa: "OPT",
    university: "New York University",
    img: "/students/s4.jpg",
  },
  {
    quote: "Being an international PhD student, I thought big tech was out of reach without connections. SKVY Harmony connected me directly with a hiring manager at a stealth AI lab in Palo Alto. Signed my H-1B offer three weeks later.",
    name: "Vikram Shah",
    role: "AI Research Engineer",
    company: "Stealth AI Lab, Palo Alto",
    visa: "H-1B",
    university: "Georgia Institute of Technology",
    img: "/students/s3.jpg",
  },
  {
    quote: "I was on CPT during my final semester and worried I'd have nothing lined up. SKVY Harmony placed me at an Austin fintech unicorn as a Cloud Engineer with a full-time offer waiting the day I graduated. Zero gap between graduation and my US career.",
    name: "Rohan Bhattacharya",
    role: "Cloud Engineer",
    company: "Fintech Unicorn, Austin",
    visa: "CPT → OPT",
    university: "University of Maryland",
    img: "/students/s2.jpg",
  },
];

// ===== Counters =====
export const COUNTERS = [
  { value: 1000, suffix: "+", label: "Students Placed" },
  { value: 1200, suffix: "+", label: "US Employer Partners" },
  { value: 95, suffix: "%", label: "Offer Acceptance" },
  { value: 92, suffix: "%", label: "OPT → H-1B Success" },
];

// ===== FAQ (10) =====
export const FAQS = [
  { q: "Do you work with international students on OPT and CPT?", a: "Yes — international students on OPT, STEM OPT, CPT and F-1 are our core focus. Every employer in our network is vetted to sponsor and value these statuses." },
  { q: "Which US employers actively sponsor H-1B through SKVY Harmony?", a: "We work with 1,200+ US employers ranging from Fortune 500 enterprises and Big-4 consulting firms to Series B-D scale-ups. Every employer on our shortlist has an active H-1B sponsorship history." },
  { q: "How long does it typically take to get placed?", a: "It varies by role, stack and market conditions — but most engaged candidates begin interviewing within 2-4 weeks of joining SKVY Harmony. We do not guarantee timelines, but we do guarantee effort, transparency and direct recruiter access." },
  { q: "Do you help with resume writing and LinkedIn optimization?", a: "Absolutely. Every candidate gets a senior recruiter-led resume rewrite (ATS-optimized) and a full LinkedIn overhaul — headline, banner, About and experience — as part of our onboarding." },
  { q: "What kind of interview preparation do you offer?", a: "1:1 mock interviews with FAANG-hired mentors across behavioural, DSA, system design and case rounds. You get live feedback plus recordings for review." },
  { q: "Do you charge students any upfront fees?", a: "Our placement services are structured to align with your success — we walk you through the exact fee structure on your consultation call so there are no surprises." },
  { q: "Can you help with the cap-gap and H-1B transfer?", a: "Yes. Our career advisors work closely with immigration attorneys to guide you through cap-gap extensions, H-1B transfers and eventual Green Card sponsorship strategy." },
  { q: "What tech stacks and roles do you place for?", a: "Software engineering, data science and analytics, AI/ML, cloud (AWS/GCP/Azure), DevOps, cybersecurity, business analysis and QA automation — across mid-to-senior levels." },
  { q: "Do you place candidates in remote or onsite roles?", a: "Both. Our network includes fully remote, hybrid and onsite roles across major US metros — SF Bay Area, NYC, Austin, Dallas, Seattle, Boston, Chicago and more." },
  { q: "How do I get started with SKVY Harmony?", a: "Book a free 30-minute consultation with a senior career advisor or share your resume — we'll review it within 24 hours and outline a personalized roadmap." },
];

// ===== Blog posts =====
export const BLOG_POSTS = [
  {
    slug: "opt-to-h1b-transition-2026",
    title: "The Complete OPT to H-1B Playbook for 2026",
    excerpt: "Timeline, cap-gap, employer selection and the mistakes that quietly cost international students their US careers.",
    date: "Dec 4, 2025",
    readTime: "9 min read",
    author: "Neha Iyer",
    tag: "Visa Strategy",
    img: "https://images.unsplash.com/photo-1579567761406-4684ee0c75b6?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    slug: "resume-that-lands-faang",
    title: "The Resume Formula That Lands FAANG Interviews",
    excerpt: "The exact structure our resume team uses to move students from silent applies to onsite loops.",
    date: "Nov 27, 2025",
    readTime: "7 min read",
    author: "Ravi Krishnan",
    tag: "Resume",
    img: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    slug: "us-tech-salary-benchmarks-2026",
    title: "US Tech Salary Benchmarks — Q4 2025",
    excerpt: "What SDEs, MLEs and Data Scientists are actually earning in SF Bay, NYC, Seattle and Austin.",
    date: "Nov 20, 2025",
    readTime: "10 min read",
    author: "Priya Menon",
    tag: "Benchmarks",
    img: "https://images.unsplash.com/photo-1556761175-4b46a572b786?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    slug: "cpt-internship-employer-checklist",
    title: "CPT Internships: The Employer Checklist That Protects You",
    excerpt: "Five questions to ask every CPT sponsor before you sign — and the red flags to walk away from.",
    date: "Nov 14, 2025",
    readTime: "6 min read",
    author: "Neha Iyer",
    tag: "CPT",
    img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    slug: "system-design-for-visa-candidates",
    title: "System Design Interviews: A Playbook for Visa Candidates",
    excerpt: "How to nail L4/L5 system design when every onsite carries visa-clock pressure.",
    date: "Nov 6, 2025",
    readTime: "8 min read",
    author: "Aarav Kulkarni",
    tag: "Interview Prep",
    img: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
  {
    slug: "salary-negotiation-on-h1b",
    title: "Salary Negotiation on H-1B (Without Killing the Offer)",
    excerpt: "The scripts, framing and email templates that get you a raise — even on a sponsored offer.",
    date: "Oct 30, 2025",
    readTime: "5 min read",
    author: "Ravi Krishnan",
    tag: "Negotiation",
    img: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  },
];

// ===== Problem / Solution =====
export const PROBLEMS = [
  { title: "Job boards ignore your visa", desc: "You spend months applying — and 90% never respond because they don't sponsor OPT or H-1B." },
  { title: "Generic resumes get filtered out", desc: "Your resume never reaches a human — the ATS silently rejects it before a recruiter opens it." },
  { title: "Cold applications go nowhere", desc: "Without a warm intro to the hiring manager, you're one of 800 applicants — and it shows." },
  { title: "The visa clock keeps ticking", desc: "OPT, cap-gap, STEM extension — every week counts, and generic career services move too slow." },
];

export const SOLUTIONS = [
  { title: "Only visa-friendly employers", desc: "Every employer in our network is vetted for OPT, CPT, STEM OPT and H-1B sponsorship." },
  { title: "ATS-optimized resume rewrites", desc: "Senior US recruiters rewrite your resume to pass Workday, Greenhouse and Lever screens." },
  { title: "Direct recruiter warm intros", desc: "We connect you directly to internal recruiters and hiring managers — not cold applications." },
  { title: "AI-matched, human-validated", desc: "Our engine matches your skills to 40,000+ live US requisitions — then a senior recruiter validates every submission." },
];

// ===== Top US IT companies (Trust marquee) =====
export const CLIENT_LOGOS = [
  "Google", "Microsoft", "Amazon", "Meta", "Apple", "Netflix", "Nvidia", "Salesforce",
  "Oracle", "IBM", "Cisco", "Adobe", "Uber", "Airbnb", "Stripe", "Snowflake",
];
